"use client";

import React, { useEffect, useState, useMemo } from "react";
import { useRouter } from "next/navigation";
import {
  TopOfferBanner,
  HeroBanner,
  ActiveOffers,
  DealOfTheDay,
  SidebarFilter,
  AllProducts,
  BundleAndSave,
  AboutMaker,
  MoreNeighbors,
} from "./components";
import type { FilterState } from "./components/SidebarFilter";
import { useTenant } from "@/src/contexts/TenantContext";
import type { Category, Combo, Coupon, OutletDetails, Product, Tenant } from "@/src/types/tenant";

interface ShopClientProps {
  prefix: string;
  tenant: Tenant;
  outlet: OutletDetails | null;
  categories: Category[];
  coupons: Coupon[];
  combos: Combo[];
  products: Product[];
}

/** Returns the effective selling price for filter comparison */
function effectivePrice(p: Product): number {
  return p.pricing?.offerPrice ?? p.pricing?.sellingPrice ?? 0;
}

/** True if product matches a single price-range filter id */
function matchesPriceRange(price: number, rangeId: string): boolean {
  if (rangeId === "under-300")  return price < 300;
  if (rangeId === "300-600")    return price >= 300 && price < 600;
  if (rangeId === "600-1000")   return price >= 600 && price < 1000;
  if (rangeId === "above-1000") return price >= 1000;
  return false;
}

/** Normalise a tag string for loose comparison */
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

/** True if product matches a single curator filter id */
function matchesCurator(p: Product, curatorId: string): boolean {
  const tags = (p.tags ?? []).map(norm);
  if (curatorId === "sale") {
    // "On sale" = has offer price OR MRP > selling price
    return !!(p.pricing?.offerPrice) ||
      ((p.pricing?.mrp ?? 0) > (p.pricing?.sellingPrice ?? 0));
  }
  if (curatorId === "bestsellers") {
    return p.featured === true ||
      tags.some((t) => t.includes("bestsell") || t.includes("popular") || t.includes("trending"));
  }
  if (curatorId === "new") {
    return tags.some((t) => t.includes("new") || t.includes("arrival") || t.includes("launch"));
  }
  if (curatorId === "gifting") {
    return tags.some((t) => t.includes("gift") || t.includes("gifting"));
  }
  return false;
}

export default function ShopClient({
  prefix,
  tenant,
  outlet: outletProp,
  categories,
  coupons,
  combos,
  products,
}: ShopClientProps) {
  const { setTenant } = useTenant();

  // ── Filter state (lifted from SidebarFilter) ──────────────────────────────
  const [filters, setFilters] = useState<FilterState>({
    category: "all",
    subcategory: "all",
    curators: [],
    priceRanges: [],
  });
  // Prefer the dedicated outlet object from API; fall back to product-embedded outlet
  const outlet = outletProp ?? products[0]?.outlet ?? null;
  const outletId = outlet?.outletId || "";

  useEffect(() => {
    setTenant({
      storeName: outlet?.outletName || tenant.tenantName,
      logoUrl: tenant.additionalInfo?.logoUrl ?? null,
      outletId,
      prefix,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefer outlet name over tenant name for display
  const displayName = outlet?.outletName || tenant.tenantName;

  const router = useRouter();
  
  const businessBrief = tenant.additionalInfo?.businessBrief?.trim();
  const location = [outlet?.city, outlet?.state].filter(Boolean).join(", ");
  const subtitle = [businessBrief, location].filter(Boolean).join(" · ");

  // ── Derived filtered product list ─────────────────────────────────────────
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      // Category
      if (filters.category !== "all" && p.category?._id !== filters.category) return false;

      // Subcategory
      if (filters.subcategory !== "all") {
        const hasSubcat = p.subcategories?.some((sc) => sc._id === filters.subcategory);
        if (!hasSubcat) return false;
      }

      // Price ranges (any selected range must match — OR logic)
      if (filters.priceRanges.length > 0) {
        const price = effectivePrice(p);
        const matchesAnyPrice = filters.priceRanges.some((r) => matchesPriceRange(price, r));
        if (!matchesAnyPrice) return false;
      }

      // Curator's pick (all selected curators must match — AND logic)
      if (filters.curators.length > 0) {
        const matchesAllCurators = filters.curators.every((c) => matchesCurator(p, c));
        if (!matchesAllCurators) return false;
      }

      return true;
    });
  }, [products, filters]);

  // Pick the first-listed product as deal-of-the-day (highest discount)
  const dealProduct = [...products].sort((a, b) => {
    const discA = a.pricing?.mrp && a.pricing?.offerPrice
      ? a.pricing.mrp - a.pricing.offerPrice : 0;
    const discB = b.pricing?.mrp && b.pricing?.offerPrice
      ? b.pricing.mrp - b.pricing.offerPrice : 0;
    return discB - discA;
  })[0];

  const now = Date.now();
  const activeOfferProduct = products.find(p =>
    p.pricing?.offerPrice &&
    p.pricing?.offerStartTime &&
    p.pricing?.offerEndTime &&
    now >= new Date(p.pricing.offerStartTime).getTime() &&
    now <= new Date(p.pricing.offerEndTime).getTime()
  );
  const hasSale = coupons.length > 0 || !!activeOfferProduct;
  const offerEndsAt = activeOfferProduct?.pricing?.offerEndTime;
  const saleLabel = coupons[0]
    ? `${coupons[0].name} · Up to ₹${Number(coupons[0].amount).toLocaleString("en-IN")} off`
    : "Festive Sale · Up to 40% off";

  return (
    <div
      className="w-full"
      style={{ background: "var(--rev-white)", color: "var(--techno-black)" }}
    >
      {/* Announcement bar */}
      {/* <TopOfferBanner coupons={coupons} /> */}

      {/* All page content inside the container */}
      <div className="gl-container">
        {/* Breadcrumb */}
        <nav
          className="flex items-center gap-2 overflow-x-auto whitespace-nowrap pt-5 text-[13px]"
          style={{ color: "var(--techno-black-50)", scrollbarWidth: "none" }}
          aria-label="Breadcrumb"
        >
          <a href="#" className="transition hover:text-[var(--techno-black)]">Home</a>
          <span className="opacity-50">/</span>
          {outlet?.city && (
            <>
              <a href="#" className="transition hover:text-[var(--techno-black)]">{outlet.city}</a>
              <span className="opacity-50">/</span>
            </>
          )}
          {categories[0]?.name && (
            <>
              <a href="#" className="transition hover:text-[var(--techno-black)]">{categories[0].name}</a>
              <span className="opacity-50">/</span>
            </>
          )}
          <span className="font-medium" style={{ color: "var(--techno-black)" }}>
            {displayName}
          </span>
        </nav>

        {/* Hero + Brand card */}
        <HeroBanner
          title={displayName}
          subtitle={subtitle || "Shop local favorites on GrabLocal"}
          bannerImage={tenant.additionalInfo?.banner || undefined}
          logoUrl={tenant.additionalInfo?.logoUrl || undefined}
          rating={tenant.outletRating?.averageRating}
          ratingCount={tenant.outletRating?.ratingCount}
          city={outlet?.city}
          category={categories[0]?.name}
          hasSale={hasSale}
          saleLabel={saleLabel}
          outletCoordinates={outlet?.coordinates ?? null}
        />

        {/* Active offers — only shown when coupons exist */}
        {coupons.length > 0 && <ActiveOffers coupons={coupons} tenantName={displayName} />}

        {/* Main grid: sidebar (hidden on mobile) + products */}
        <div className="mt-[clamp(40px,5vw,56px)] grid items-start gap-[clamp(32px,4vw,56px)] md:grid-cols-[240px_minmax(0,1fr)]">
          <SidebarFilter
            categories={categories}
            products={products}
            onFilterChange={setFilters}
          />
          <div>
            <AllProducts
              products={filteredProducts}
              outletId={outletId}
              limit={7}
              prefix={prefix}
            />
          </div>
        </div>

        {/* Bundle deals */}
        <BundleAndSave combos={combos} outletId={outletId} />

        {/* Meet the maker */}
        <AboutMaker
          storeName={displayName}
          description={businessBrief}
          banner={tenant.additionalInfo?.banner || undefined}
          makerUrl={tenant.additionalInfo?.landingPage?.makerUrl || undefined}
          websiteLink={tenant.additionalInfo?.website_link || undefined}
        />

        {/* Related brands */}
        <MoreNeighbors currentPrefix={prefix} />
      </div>
    </div>
  );
}
